// client/src/contexts/RewardContext.js
// Context for managing reward popups and reward items globally

import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import RewardPopup from '../components/RewardPopup';
import useRewardPopup from '../hooks/useRewardPopup';
import { getAllRewardItems } from '../services/rewardItemsService';

const RewardContext = createContext();

export const useReward = () => {
  const context = useContext(RewardContext);
  if (!context) {
    throw new Error('useReward must be used within a RewardProvider');
  }
  return context;
};

export const RewardProvider = ({ children }) => {
  const rewardPopup = useRewardPopup();
  
  // Session-level cache for reward items
  const [rewardItems, setRewardItems] = useState(null);
  const [loadingRewardItems, setLoadingRewardItems] = useState(false);
  const [rewardItemsError, setRewardItemsError] = useState(null);

  const loadRewardItems = useCallback(async (forceReload = false) => {
    // Return cached data if already loaded and not forcing reload
    if (rewardItems && !forceReload) {
      return rewardItems;
    }

    setLoadingRewardItems(true);
    setRewardItemsError(null);

    try {
      const items = await getAllRewardItems();
      setRewardItems(items);
      return items;
    } catch (error) {
      console.error('Error loading reward items:', error);
      setRewardItemsError(error.message);
      return [];
    } finally {
      setLoadingRewardItems(false);
    }
  }, [rewardItems]);

  // Force reload (for admin updates)
  const refreshRewardItems = useCallback(async () => {
    return loadRewardItems(true);
  }, [loadRewardItems]);

  // Load reward items once when provider mounts
  useEffect(() => {
    if (!rewardItems && !loadingRewardItems) {
      loadRewardItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Enhanced reward functions with asset management
  const showLevelComplete = useCallback((levelNumber, options = {}) => {
    // You can add logic here to select different assets based on level
    const assetOptions = getAssetOptions(levelNumber, 'level_complete');
    rewardPopup.showLevelComplete(levelNumber, { ...assetOptions, ...options });
  }, [rewardPopup]);

  const showCourseComplete = useCallback((courseName, options = {}) => {
    const assetOptions = getAssetOptions(null, 'course_complete');
    rewardPopup.showCourseComplete({ 
      customMessage: `Congratulations! You've completed the "${courseName}" course!`,
      ...assetOptions, 
      ...options 
    });
  }, [rewardPopup]);

  const showAchievement = useCallback((achievementName, options = {}) => {
    const assetOptions = getAssetOptions(null, 'achievement');
    rewardPopup.showAchievement(achievementName, { ...assetOptions, ...options });
  }, [rewardPopup]);

  const value = {
    ...rewardPopup,
    showLevelComplete,
    showCourseComplete,
    showAchievement,
    // Reward items cache
    rewardItems: rewardItems || [],
    loadingRewardItems,
    rewardItemsError,
    loadRewardItems,
    refreshRewardItems
  };

  return (
    <RewardContext.Provider value={value}>
      {children}
      <RewardPopup
        isVisible={rewardPopup.popupState.isVisible}
        onClose={rewardPopup.hideReward}
        levelNumber={rewardPopup.popupState.levelNumber}
        rewardType={rewardPopup.popupState.rewardType}
        customMessage={rewardPopup.popupState.customMessage}
        videoSrc={rewardPopup.popupState.videoSrc}
        gifSrc={rewardPopup.popupState.gifSrc}
        audioSrc={rewardPopup.popupState.audioSrc}
        duration={rewardPopup.popupState.duration}
      />
    </RewardContext.Provider>
  );
};

// Helper function to get asset options based on level/type
const getAssetOptions = (levelNumber, rewardType) => {
  const assetMap = {
    level_complete: {
      // Using a single GIF and audio for all level completions
      gifSrc: '/images/rewards/reward_gif.gif',
      audioSrc: '/images/rewards/reward_gif_audio.mp3'
    },
    course_complete: {
      // Using the same GIF and audio for course completions
      gifSrc: '/images/rewards/reward_gif.gif',
      audioSrc: '/images/rewards/reward_gif_audio.mp3'
    },
    achievement: {
      gifSrc: '/images/rewards/achievement-unlocked.gif'
    }
  };

  return assetMap[rewardType] || {};
};

export default RewardContext;
